import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuPlus, LuX } from 'react-icons/lu';
import api from '../../services/api';
import { limpiarSerie } from '../../utils/serie';
import ModalShell from '../ui/ModalShell';
import UbicacionEnMapa from '../mapa/UbicacionEnMapa';
import { getTodayISO } from '../../utils/dateUtils';
import { AvisoParecidos, CodigoYOrigen } from './DatosClienteEmpresa';

// Alta / edición de un cliente del Portal Empresa (13-oct-2026).
// 18-oct-2026: código de cliente de la empresa (ej. "2041"), los equipos que hay en esa dirección en el
// mismo paso (sin N/S se les genera uno) y "otra dirección" de un cliente que ya está cargado.
// 21-oct-2026: origen (instalación nueva / del lote), código automático con la fecha de instalación
// y aviso de nombres parecidos (¿otra dirección o un cliente distinto?).
const INPUT = 'w-full h-11 px-3.5 rounded-xl bg-chip text-ink text-body outline-none placeholder:text-muted';
const ROTULO = 'text-label font-black uppercase tracking-widest text-muted';
const VACIO = () => ({ numeroSerie: '', modelo: '', ubicacion: '' });

function FilasEquipos({ filas, setFilas }) {
    const cambiar = (i, k) => e => setFilas(l => l.map((f, j) => (j === i ? { ...f, [k]: k === 'numeroSerie' ? limpiarSerie(e.target.value) : e.target.value } : f)));
    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <p className={ROTULO}>Equipos en esta dirección ({filas.length})</p>
                <button type="button" onClick={() => setFilas(l => [...l, VACIO()])} disabled={filas.length >= 30}
                    className="h-8 px-2.5 rounded-lg bg-chip text-label font-black text-ink inline-flex items-center gap-1 disabled:opacity-40"><LuPlus size={14} /> Equipo</button>
            </div>
            {filas.length === 0 && <p className="text-caption text-muted">Si ya sabés qué equipos hay, sumalos acá. Si es una instalación nueva, dejá el N/S vacío y se genera solo.</p>}
            {filas.map((f, i) => (
                <div key={i} className="p-2.5 rounded-xl bg-panel space-y-2">
                    <div className="flex gap-2">
                        <input className={INPUT} placeholder="N/S (vacío = se genera solo)" value={f.numeroSerie} onChange={cambiar(i, 'numeroSerie')} autoCapitalize="characters" />
                        <button type="button" onClick={() => setFilas(l => l.filter((_, j) => j !== i))} aria-label="Quitar"
                            className="shrink-0 w-11 h-11 rounded-xl bg-chip text-muted inline-flex items-center justify-center"><LuX size={16} /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <input className={INPUT} placeholder="Modelo" value={f.modelo} onChange={cambiar(i, 'modelo')} />
                        <input className={INPUT} placeholder="Dónde está (ej. cocina)" value={f.ubicacion} onChange={cambiar(i, 'ubicacion')} />
                    </div>
                </div>
            ))}
        </div>
    );
}

// inicial: cliente a editar (con id) | { otraDe: cliente } para cargarle otra dirección | null
export default function FormClienteEmpresa({ inicial, onCerrar, onGuardado }) {
    const otraDe = inicial?.otraDe || null;
    const editando = Boolean(inicial?.id);
    const [c, setC] = useState(() => ({
        nombre: '', codigo: '', calle: '', numero: '', piso: '', depto: '', localidad: '', notas: '',
        origen: 'INSTALACION', instaladoEl: getTodayISO(),
        ...(editando ? { ...inicial, origen: inicial.origen || 'INSTALACION', instaladoEl: inicial.instaladoEl || '' } : {}),
        ...(otraDe ? { nombre: otraDe.nombre, codigo: otraDe.codigo || '' } : {}),
    }));
    const [decision, setDecision] = useState(null);     // ver AvisoParecidos
    const [parecidos, setParecidos] = useState([]);
    const cambioNombre = !editando || c.nombre.trim().toLowerCase() !== String(inicial.nombre || '').trim().toLowerCase();
    const madre = otraDe || (decision?.tipo === 'otra' ? decision.cliente : null);
    const [filas, setFilas] = useState([]);
    const [punto, setPunto] = useState(null);
    const [guardando, setGuardando] = useState(false);
    const set = (k) => (e) => setC(x => ({ ...x, [k]: e && e.target ? e.target.value : e }));
    const listo = c.nombre.trim().length >= 2 && (c.calle || '').trim() && (c.localidad || '').trim();
    const guardar = async () => {
        if (!listo) { toast.error('Completá nombre, calle y localidad'); return; }
        if (!otraDe && cambioNombre && parecidos.length && !decision) { toast.error('Indicá si es otra dirección de ese cliente o un cliente distinto'); return; }
        setGuardando(true);
        try {
            const cuerpo = { ...Object.fromEntries(['nombre', 'codigo', 'calle', 'numero', 'piso', 'depto', 'localidad', 'notas', 'origen', 'instaladoEl'].map(k => [k, (c[k] || '').trim() || null])),
                ...(madre ? { otraDe: madre.id, nombre: madre.nombre, codigo: madre.codigo || null } : {}),
                ...(decision?.tipo === 'distinto' ? { distinto: true } : {}),
                ...(punto ? { lat: punto.lat, lng: punto.lng } : {}), ...(!editando && filas.length ? { equipos: filas } : {}) };
            if (editando) {
                await api.put(`/empresa/clientes/${inicial.id}`, cuerpo);
                toast.success('Cliente actualizado');
            } else {
                const r = await api.post('/empresa/clientes', cuerpo);
                const series = r.data?.equipos || [];
                const cod = r.data?.codigo ? `${r.data.codigo} - ` : '';
                toast.success(series.length ? `${cod}${r.data?.nombre} cargado con ${series.length} equipo${series.length !== 1 ? 's' : ''}: ${series.join(', ')}` : (madre ? 'Dirección agregada' : `${cod}${r.data?.nombre || 'Cliente'} cargado`));
            }
            onGuardado();
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo guardar'); setGuardando(false); }
    };
    const titulo = editando ? 'Editar cliente' : otraDe ? `Otra dirección de ${otraDe.codigo ? `${otraDe.codigo} - ` : ''}${otraDe.nombre}` : 'Cliente nuevo';
    return (
        <ModalShell titulo={titulo} subtitulo="Con la dirección bien marcada llegamos sin vueltas" onCerrar={onCerrar}
            pie={<button type="button" onClick={guardar} disabled={guardando || !listo}
                className="w-full h-12 rounded-xl bg-brand-red text-white text-label font-black disabled:opacity-40">{guardando ? 'Guardando…' : 'Guardar'}</button>}>
            <div className="space-y-2">
                {!otraDe && (
                    <>
                        <input className={INPUT} placeholder="Nombre del cliente *" value={c.nombre} autoFocus={!editando}
                            onChange={e => { set('nombre')(e); setDecision(null); }} />
                        {cambioNombre && <AvisoParecidos nombre={c.nombre} excluir={editando ? inicial.id : null} decision={decision}
                            onDecidir={setDecision} onParecidos={setParecidos} />}
                        {!madre && <CodigoYOrigen c={c} set={set} editando={editando} />}
                    </>
                )}
                <div className="grid grid-cols-[1fr_5.5rem] gap-2">
                    <input className={INPUT} placeholder="Calle *" value={c.calle || ''} onChange={set('calle')} autoFocus={Boolean(otraDe)} />
                    <input className={INPUT} placeholder="Nro" inputMode="numeric" value={c.numero || ''} onChange={set('numero')} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <input className={INPUT} placeholder="Piso" value={c.piso || ''} onChange={set('piso')} />
                    <input className={INPUT} placeholder="Depto" value={c.depto || ''} onChange={set('depto')} />
                </div>
                <input className={INPUT} placeholder="Localidad o barrio *" value={c.localidad || ''} onChange={set('localidad')} />
                <UbicacionEnMapa endpoint="/empresa/ubicar" onCambio={setPunto}
                    onDireccion={d => setC(x => ({ ...x, calle: d.calle || x.calle, numero: d.numero, localidad: d.localidad || x.localidad }))}
                    direccion={[c.calle, c.numero].filter(Boolean).join(' ') + (c.localidad ? `, ${c.localidad}` : '')} />
                <textarea rows={2} value={c.notas || ''} onChange={set('notas')} placeholder="A quién preguntar, horario de acceso… (opcional)"
                    className="w-full px-3.5 py-3 rounded-xl bg-chip text-body text-ink outline-none resize-none placeholder:text-muted" />
                {!editando && <div className="pt-2"><FilasEquipos filas={filas} setFilas={setFilas} /></div>}
            </div>
        </ModalShell>
    );
}
